// src/components/admin/PlatformAnalyticsWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Grid,
  Paper,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Schedule as TimeIcon,
  Devices as DeviceIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as LineChartIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const PlatformAnalyticsWidget = ({ data }) => {
  const [tabValue, setTabValue] = useState(0);

  if (!data) return null;

  const trafficMetrics = [
    {
      name: 'Páginas Vistas',
      value: data.pageViews?.toLocaleString() || '0',
      trend: '+15.3%',
      trendUp: true,
      icon: <ViewIcon />
    },
    {
      name: 'Visitantes Únicos',
      value: data.uniqueVisitors?.toLocaleString() || '0',
      trend: '+8.7%',
      trendUp: true,
      icon: <PeopleIcon />
    },
    {
      name: 'Tasa de Rebote',
      value: `${data.bounceRate}%` || '0%',
      trend: '-5.2%',
      trendUp: false,
      icon: <TrendingDownIcon />
    },
    {
      name: 'Duración Sesión',
      value: `${Math.floor((data.avgSessionDuration || 0) / 60)}:${((data.avgSessionDuration || 0) % 60).toString().padStart(2, '0')}`,
      trend: '+12.4%',
      trendUp: true,
      icon: <TimeIcon />
    }
  ];

  const topPages = data.topPages || [
    { page: '/dashboard', views: 8934, percentage: 34.8 },
    { page: '/events', views: 6723, percentage: 26.2 },
    { page: '/profile', views: 4521, percentage: 17.6 },
    { page: '/registration', views: 3456, percentage: 13.5 },
    { page: '/results', views: 1986, percentage: 7.9 }
  ];

  const deviceStats = [
    { device: 'Desktop', percentage: 65.4, color: 'primary' },
    { device: 'Mobile', percentage: 28.7, color: 'success' },
    { device: 'Tablet', percentage: 5.9, color: 'warning' }
  ];

  const userBehavior = [
    { action: 'Registro de eventos', count: 1243, trend: '+18%' },
    { action: 'Consulta de resultados', count: 2156, trend: '+12%' },
    { action: 'Actualización de perfil', count: 876, trend: '+5%' },
    { action: 'Descarga de certificados', count: 543, trend: '+25%' },
    { action: 'Compartir en redes', count: 321, trend: '+8%' }
  ];

  const geographicData = [
    { country: 'México', users: 2340, percentage: 68.2 },
    { country: 'Colombia', users: 456, percentage: 13.3 },
    { country: 'Argentina', users: 234, percentage: 6.8 },
    { country: 'España', users: 198, percentage: 5.8 },
    { country: 'Otros', users: 203, percentage: 5.9 }
  ];

  const performanceMetrics = [
    { metric: 'Tiempo de Carga', value: '1.2s', status: 'excellent', target: '< 2s' },
    { metric: 'First Paint', value: '0.8s', status: 'excellent', target: '< 1s' },
    { metric: 'Interactividad', value: '2.1s', status: 'good', target: '< 3s' },
    { metric: 'Core Web Vitals', value: '95%', status: 'excellent', target: '> 90%' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      excellent: 'success',
      good: 'info',
      warning: 'warning',
      poor: 'error'
    };
    return colors[status] || 'default';
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const exportAnalytics = () => {
    console.log('Exportando analíticas...');
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="📈 Analíticas de la Plataforma"
        subheader="Estadísticas de uso y rendimiento"
        action={
          <Box display="flex" gap={1}>
            <Tooltip title="Exportar reporte">
              <IconButton size="small" onClick={exportAnalytics}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Actualizar datos">
              <IconButton size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Chip 
              label="Últimas 24h" 
              color="primary" 
              size="small"
            />
          </Box>
        }
      />
      
      <CardContent>
        {/* Tabs para diferentes vistas */}
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Tráfico" />
          <Tab label="Comportamiento" />
          <Tab label="Rendimiento" />
        </Tabs>

        {/* Tab 1: Tráfico y audiencia */}
        {tabValue === 0 && (
          <Box>
            {/* Métricas principales de tráfico */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {trafficMetrics.map((metric, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                    <Box display="flex" justifyContent="center" mb={1}>
                      {metric.icon}
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                      {metric.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      {metric.name}
                    </Typography>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                      {metric.trendUp ? (
                        <TrendingUpIcon color="success" fontSize="small" />
                      ) : (
                        <TrendingDownIcon color="error" fontSize="small" />
                      )}
                      <Typography 
                        variant="caption" 
                        color={metric.trendUp ? 'success.main' : 'error.main'}
                      >
                        {metric.trend}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Páginas más visitadas */}
            <Typography variant="subtitle2" gutterBottom>
              📄 Páginas Más Visitadas
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Página</TableCell>
                    <TableCell align="right">Visitas</TableCell>
                    <TableCell align="right">%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topPages.map((page, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {page.page}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {page.views.toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={page.percentage}
                            sx={{ width: 50, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption">
                            {page.percentage}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Dispositivos y geografía */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom>
                  📱 Dispositivos
                </Typography>
                {deviceStats.map((device, index) => (
                  <Box key={index} sx={{ mb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{device.device}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {device.percentage}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={device.percentage}
                      color={device.color}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                ))}
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom>
                  🌍 Geografía
                </Typography>
                <List dense>
                  {geographicData.slice(0, 4).map((country, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <LocationIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={country.country}
                        secondary={`${country.users} usuarios`}
                      />
                      <Typography variant="caption">
                        {country.percentage}%
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 2: Comportamiento de usuarios */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              👤 Acciones Más Populares
            </Typography>
            
            <List>
              {userBehavior.map((behavior, index) => (
                <ListItem key={index} divider>
                  <ListItemIcon>
                    <BarChartIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight="bold">
                          {behavior.action}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            {behavior.count.toLocaleString()}
                          </Typography>
                          <Chip
                            label={behavior.trend}
                            color="success"
                            size="small"
                            sx={{ fontSize: '0.6rem', height: 20 }}
                          />
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                💡 Insights de Comportamiento
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Los usuarios pasan en promedio {Math.floor(data.avgSessionDuration / 60)} minutos en la plataforma
                <br />
                • El 78% de los usuarios registrados completan al menos una acción
                <br />
                • Los horarios pico son de 9-11 AM y 3-5 PM
                <br />
                • Las descargas de certificados han aumentado 25% este mes
              </Typography>
            </Box>
          </Box>
        )}

        {/* Tab 3: Rendimiento */}
        {tabValue === 2 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              ⚡ Métricas de Rendimiento
            </Typography>
            
            <Grid container spacing={2}>
              {performanceMetrics.map((metric, index) => (
                <Grid item xs={6} key={index}>
                  <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      {metric.metric}
                    </Typography>
                    <Typography variant="h5" color={`${getStatusColor(metric.status)}.main`} gutterBottom>
                      {metric.value}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Objetivo: {metric.target}
                      </Typography>
                      <Chip
                        label={metric.status}
                        color={getStatusColor(metric.status)}
                        size="small"
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                📊 Resumen de Rendimiento
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2, color: 'success.dark' }}>
                <Typography variant="body2">
                  <strong>Estado General: Excelente</strong>
                  <br />
                  La plataforma cumple con todos los estándares de rendimiento web.
                  Tiempo de respuesta del servidor: 120ms promedio.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PlatformAnalyticsWidget;